import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  downloadIntelligenceCsvTemplate,
  downloadIntelligenceTemplate,
  fetchIntelligenceLimits,
  fetchSubmittedPortfolio,
  parseIntelligencePortfolio,
} from "@services/networkIntelligence";
import FacilityNetworkPageShell from "../facilityNetwork/FacilityNetworkPageShell";
import { FN } from "../facilityNetwork/facilityNetworkTheme";
import { beginPortfolioHandoff } from "./portfolioHandoff";

const FORMAT_ROWS = [
  { label: "Facility name", hint: "required" },
  { label: "Address", hint: "required" },
  { label: "CCN", hint: "at least one of CCN or NPI" },
  { label: "NPI", hint: "at least one of CCN or NPI" },
  {
    label: "Intent",
    hint: "required · Owned network, Acquisition target, Market watch, Competitor benchmark, Other",
  },
  { label: "Note", hint: "optional" },
] as const;

export default function NetworkIntelligenceUpload() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [maxFacilities, setMaxFacilities] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchIntelligenceLimits()
      .then((limits) => {
        if (!cancelled) setMaxFacilities(limits.max_facilities);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Could not load portfolio capacity");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchSubmittedPortfolio()
      .then((portfolio) => {
        if (!cancelled) setHasSubmitted(portfolio != null);
      })
      .catch(() => {
        if (!cancelled) setHasSubmitted(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const goLanding = () => {
    navigate("/dashboard/network/intel", {
      state: { currentView: "network-intelligence" },
    });
  };

  const goStatus = () => {
    navigate("/dashboard/network/intel", {
      state: { currentView: "network-intelligence", portfolioView: "status" },
    });
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    const lowered = file.name.toLowerCase();
    if (!lowered.endsWith(".xlsx") && !lowered.endsWith(".csv")) {
      setError("File must be .csv or .xlsx");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const portfolio = await parseIntelligencePortfolio(file);
      navigate("/dashboard/network/intel/review", {
        state: {
          currentView: "network-intelligence",
          portfolio,
          handoffToken: beginPortfolioHandoff(),
        },
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not read spreadsheet");
    } finally {
      setBusy(false);
    }
  };

  const onDownload = async () => {
    setError("");
    try {
      await downloadIntelligenceTemplate();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not download template");
    }
  };

  return (
    <FacilityNetworkPageShell>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 880 }}>
        <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 12, color: FN.figmaAccent }}>
          <Button
            onClick={goLanding}
            sx={{
              p: 0,
              minWidth: 0,
              textTransform: "none",
              fontFamily: FN.fontFamily,
              fontSize: 12,
              color: FN.primary,
            }}
          >
            Intelligence
          </Button>
          {hasSubmitted ? (
            <>
              {" › "}
              <Button
                onClick={goStatus}
                sx={{
                  p: 0,
                  minWidth: 0,
                  textTransform: "none",
                  fontFamily: FN.fontFamily,
                  fontSize: 12,
                  color: FN.primary,
                }}
              >
                Portfolio status
              </Button>
            </>
          ) : null}
          {" › Submit portfolio"}
        </Typography>

        <Box>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: FN.figmaTitleSize,
              fontWeight: 600,
              color: FN.title,
              lineHeight: "32px",
            }}
          >
            Submit your Intelligence portfolio
          </Typography>
          <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 13, color: FN.figmaAccent }}>
            Excel or CSV upload · reviewed within 72 hours
          </Typography>
        </Box>

        <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 14, color: FN.subtext, lineHeight: 1.6 }}>
          Upload an Excel or CSV file of the facilities you want in your Intelligence dashboards. Our
          team validates data quality and confirms no unintended access within 72 hours, then
          dashboards go live. Public regulatory data is pulled for each facility — consent is not
          required.
        </Typography>

        <Box
          sx={{
            bgcolor: "#f3fdff",
            borderLeft: `3px solid ${FN.primary}`,
            borderRadius: "4px",
            px: 2,
            py: 1.5,
          }}
        >
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: FN.pressed,
              mb: 1,
            }}
          >
            File format
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {FORMAT_ROWS.map((row) => (
              <Typography
                component="li"
                key={row.label}
                sx={{ fontFamily: FN.fontFamily, fontSize: 13, color: FN.text, lineHeight: 1.8 }}
              >
                <strong>{row.label}</strong>
                <Box component="span" sx={{ ml: 1, color: FN.figmaAccent, fontSize: 12 }}>
                  {row.hint}
                </Box>
              </Typography>
            ))}
          </Box>
          <Button
            onClick={onDownload}
            sx={{
              mt: 1,
              p: 0,
              minWidth: 0,
              textTransform: "none",
              fontFamily: FN.fontFamily,
              fontWeight: 600,
              color: FN.primary,
            }}
          >
            Download Excel template
          </Button>
          <Button
            onClick={() => {
              setError("");
              downloadIntelligenceCsvTemplate().catch((err: unknown) => {
                setError(err instanceof Error ? err.message : "Could not download CSV template");
              });
            }}
            sx={{
              mt: 1,
              ml: 2,
              p: 0,
              minWidth: 0,
              textTransform: "none",
              fontFamily: FN.fontFamily,
              fontWeight: 600,
              color: FN.primary,
            }}
          >
            Download CSV template
          </Button>
        </Box>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Box
          data-testid="ni-upload-dropzone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            void handleFile(event.dataTransfer.files?.[0]);
          }}
          sx={{
            border: `2px dashed ${dragging ? FN.primary : FN.shellBorder}`,
            borderRadius: "8px",
            bgcolor: dragging ? "#f3fdff" : "#fff",
            py: 6,
            px: 2,
            textAlign: "center",
            cursor: busy ? "progress" : "pointer",
          }}
        >
          <input
            ref={inputRef}
            hidden
            type="file"
            aria-label="Portfolio file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(event) => {
              void handleFile(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
          {busy ? <CircularProgress size={28} /> : null}
          <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 15, color: FN.text }}>
            Drop your Excel or CSV file here, or <strong>click to browse</strong>
          </Typography>
          <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 12, color: FN.figmaAccent, mt: 0.5 }}>
            {maxFacilities == null
              ? ".csv or .xlsx"
              : `Up to ${maxFacilities} facilities · .csv or .xlsx`}
          </Typography>
        </Box>

        <Box sx={{ bgcolor: "#f3fdff", borderRadius: "4px", px: 2, py: 1.25 }}>
          <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 12, color: FN.figmaAccent }}>
            {maxFacilities == null
              ? "Loading portfolio capacity…"
              : `You can submit up to ${maxFacilities} rows`}
          </Typography>
        </Box>
      </Box>
    </FacilityNetworkPageShell>
  );
}
